import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TaskStatus, TaskType } from '@monorepo/shared';
import type { TaskDto } from '@monorepo/shared';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import {
  useGetTasks,
  useSendTaskReminder,
  type TaskQueryParams,
  type TaskScopeTab,
} from '../services/tasks.service';
import { useGetDocumentPresignedUrl } from '../services/documents.service';
import { useDebouncedValue } from './useDebouncedValue';
import { hasPermission, Permission } from '../utils/permissions';
import { queryClient } from '../lib/queryClient';
import type { DiffuseDocumentModalRef } from '../components/documents/DiffuseDocumentModal';
import type { TaskValidationModalRef } from '../components/tasks/TaskValidationModal';

const TASKS_PER_PAGE = 10;

export const useMyTasksController = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const canViewAllTasks =
    !!currentUserRole && hasPermission(currentUserRole, Permission.VALIDATE_DOCUMENT);

  const activeTab: TaskScopeTab =
    searchParams.get('tab') === 'all' && canViewAllTasks ? 'all' : 'mine';

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TaskType | ''>('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [userFilter, setUserFilter] = useState('');
  const debouncedSearch = useDebouncedValue(search);

  const diffuseModalRef = useRef<DiffuseDocumentModalRef>(null);
  const validationModalRef = useRef<TaskValidationModalRef>(null);
  const presignedUrlMutation = useGetDocumentPresignedUrl(selectedCompanyId || '');
  const sendReminderMutation = useSendTaskReminder(selectedCompanyId || '');
  const [pendingReminderTaskId, setPendingReminderTaskId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, typeFilter, statusFilter, userFilter, activeTab]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const handleFocus = () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', selectedCompanyId] });
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [selectedCompanyId]);

  const params: TaskQueryParams = {
    ...(typeFilter ? { type: typeFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(activeTab === 'all' && userFilter ? { userCompanyId: userFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    page: currentPage,
    limit: TASKS_PER_PAGE,
  };

  const { data, isLoading, error } = useGetTasks(selectedCompanyId || '', activeTab, params);

  const userOptions = useMemo(() => {
    const optionMap = new Map<string, string>();
    (data?.tasks ?? []).forEach((task) => {
      if (task.user) {
        optionMap.set(task.user.userCompanyId, `${task.user.firstName} ${task.user.lastName}`);
      }
    });
    return Array.from(optionMap.entries()).map(([id, label]) => ({ id, label }));
  }, [data]);

  const handleTabChange = (tab: TaskScopeTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
  };

  const handleResetFilters = () => {
    setSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setUserFilter('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    if (selectedCompanyId) {
      queryClient.invalidateQueries({ queryKey: ['tasks', selectedCompanyId] });
    }
  };

  const handleTaskAction = (task: TaskDto) => {
    if (task.type === TaskType.READ && task.accessToken) {
      window.open(`/d/${task.accessToken}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (task.type === TaskType.VALIDATION && task.documentId) {
      validationModalRef.current?.open(task.documentId, task.subtitle, task.nodeId);
      return;
    }
    if (task.type === TaskType.DIFFUSION && task.documentId) {
      diffuseModalRef.current?.open(task.documentId, task.subtitle);
    }
  };

  const handleSendReminder = (task: TaskDto) => {
    setPendingReminderTaskId(task.id);
    sendReminderMutation.mutate(task.id, {
      onSettled: () => setPendingReminderTaskId(null),
    });
  };

  const handleViewDocument = (task: TaskDto) => {
    if (!task.nodeId) return;
    presignedUrlMutation.mutate(task.nodeId, {
      onSuccess: (result) => window.open(result.url, '_blank', 'noopener,noreferrer'),
    });
  };

  return {
    selectedCompanyId,
    currentUserRole,
    canViewAllTasks,
    activeTab,
    currentPage,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    userFilter,
    setUserFilter,
    userOptions,
    tasksData: data,
    isTasksLoading: isLoading,
    tasksError: !!error,
    diffuseModalRef,
    validationModalRef,
    setCurrentPage,
    handleTabChange,
    handleResetFilters,
    handleRefresh,
    handleTaskAction,
    handleViewDocument,
    handleSendReminder,
    pendingReminderTaskId,
  };
};
